import React, { useEffect, useRef, useState } from "react";
import "./CarouselBanner.css";
import { Link } from "react-router-dom";

interface CarouselBannerProps {
    images: { src: string, link?: string }[];
    duration?: number; // in ms
    showButtons?: boolean;
}

const CarouselBanner: React.FC<CarouselBannerProps> = ({ images, duration = 3000, showButtons = true }) => {
    const [current, setCurrent] = useState(0);
    const [transition, setTransition] = useState<"enter" | "active" | "exit">("active");
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const prevIndex = useRef(current);

    useEffect(() => {
        prevIndex.current = current;
    }, [current]);

    useEffect(() => {
        setTransition("active");
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setTransition("exit");
            setTimeout(() => {
                setCurrent((prev) => (prev + 1) % images.length);
                setTransition("enter");
                setTimeout(() => setTransition("active"), 50);
            }, 700);
        }, duration);
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [current, duration, images.length]);

    const goTo = (idx: number) => {
        if (idx === current) return;
        setTransition("exit");
        setTimeout(() => {
            setCurrent(idx);
            setTransition("enter");
            setTimeout(() => setTransition("active"), 50);
        }, 700);
    };

    const prev = () => goTo((current - 1 + images.length) % images.length);
    const next = () => goTo((current + 1) % images.length);

    return (
        <div className="carousel-banner-container">
            <div className="carousel-banner-images-row">
                {images.map((img, idx) => (
                    <>
                        {
                            img.link ? (
                                <Link to={img.link} key={idx}>
                                    <img
                                        src={img.src}
                                        alt={`banner-${idx}`}
                                        className={`carousel-banner-image${idx === current ? ` ${transition} active` : ""}`}
                                        style={{ display: idx === current ? "block" : "none" }}
                                    />
                                </Link>
                            ) : 
                            (
                                <img
                                    key={idx}
                                    src={img.src}
                                    alt={`banner-${idx}`}
                                    className={`carousel-banner-image${idx === current ? ` ${transition} active` : ""}`}
                                    style={{ display: idx === current ? "block" : "none" }}
                                />
                            )
                        }
                    </>
                ))}
            </div>
            {
                showButtons &&
                (
                    <>
                        <button className="carousel-banner-arrow left" onClick={prev} aria-label="Previous banner">&#8592;</button>
                        <button className="carousel-banner-arrow right" onClick={next} aria-label="Next banner">&#8594;</button>        
                    </>
                )
            }
            <div className="carousel-banner-indicators">
                {images.map((_, idx) => (
                    <span
                        key={idx}
                        className={`carousel-banner-dot${idx === current ? " active" : ""}`}
                        onClick={() => goTo(idx)}
                        aria-label={`Go to banner ${idx + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default CarouselBanner;